import { resolveAt } from '../pricing/resolveAt';
import { compareQuarters } from '../constants/quarterConfig';
import { solveBaseMicros, InfeasibleBaseError } from '../pricing/backDerive';
import { snapshotFromSeed, type CatalogSeedRow } from '../pricing/seedBuilder';
// rollupSplit, NOT exemptMicrosOf: the solve needs BOTH halves of the split.
// See Step 0 and the pass-2 body below.
import { deriveHwPricing, rollupSplit } from '../pricing/engine';
// mulDivHalfEven: the same canonical-zero-remainder guard remainderRows.ts
// already applies (see the pass-2 body below), reused rather than reinvented.
import { mulDivHalfEven } from '../pricing/money';
// Import only what the file uses — CRA promotes an unused-var lint warning to a
// build failure under CI=true. Drop any of these that your implementation
// does not actually reference.
import { isCostKind } from './costDraft';
import type { AuthoredRow, AuthoredSeed, Blocked, CostCell, MatchedDraft, NoOp, PlannedRow }
  from './costDraft';

/** recordedAt for a row that exists only in memory. `resolveAt` selects on
 *  (effectiveFrom, version) and NEVER on recordedAt (`resolveAt.ts:55-56`), so
 *  this cannot change which row wins; it only has to survive the `asOf`
 *  transaction-time filter (`resolveAt.ts:53`). Hence `asOf ?? sentinel` in
 *  toSeedRow: stamping a bare '9999-…' while an asOf is in force would filter
 *  the staged row back out and silently price against the UNCHANGED catalog. */
const STAGED_RECORDED_AT = '9999-12-31T00:00:00Z';

/** '1234.56' + scale 6 -> '1234560000'. String arithmetic: a float here would
 *  reintroduce the rounding the cent-exact parity gates forbid.
 *
 *  Validates BEFORE parsing (review finding #3): `costDraft.ts`'s doc comment
 *  on `CostCell.amount` assigns normalization to the reader, but `planWrites`
 *  is also the manual editor's write path, so a stray separator or currency
 *  symbol can reach here un-stripped. Without this check, `'1,234.56'` would
 *  silently split into `whole='1,234'` and return the malformed digit string
 *  `'1,23456'` straight into a `PlannedRow.value` — surfacing only much
 *  later as `Number(...) -> NaN` inside `snapshotFromSeed`. Both this throw
 *  and the precision throw below are caught at the two call sites and turned
 *  into a `Blocked` entry, never an uncaught abort of the whole plan. */
export function scaleDecimal(amount: string, scale: number): string {
  if (!/^-?\d+(\.\d+)?$/.test(amount)) {
    throw new Error(`${amount} is not a plain decimal number`);
  }
  const negative = amount.startsWith('-');
  const [whole, frac = ''] = amount.replace('-', '').split('.') as [string, string?];
  if (frac.length > scale) throw new Error(`${amount} has more precision than scale ${scale}`);
  const digits = `${whole}${frac.padEnd(scale, '0')}`.replace(/^0+(?=\d)/, '');
  return `${negative ? '-' : ''}${digits}`;
}

export function planWrites(
  matched: MatchedDraft, seed: AuthoredSeed, asOf?: string,
): { rows: PlannedRow[]; noOps: NoOp[]; blocked: Blocked[] } {
  const rows: PlannedRow[] = [];
  const noOps: NoOp[] = [];
  const blocked: Blocked[] = [];
  // Provenance for the duplicate-tuple guard below: PlannedRow itself carries
  // no cell (a component write and a product-solved write shouldn't both have
  // to know their own origin), so this tracks it by object identity for every
  // row this function pushes, from either pass.
  const cellOf = new Map<PlannedRow, CostCell>();

  /** Resolve the kind AT THE CELL'S QUARTER, never last-row-wins.
   *  A flat `for (const r of seed.rows) map.set(r.componentSku, r.kind)` takes
   *  whatever comes last in array order, and `buildSeedFromProductDb` pushes its
   *  `unpriced` terminators in a SEPARATE FINAL LOOP (`seedBuilder.ts:208-218`).
   *  So for every terminated SKU that map ends up holding 'unpriced' — and a PM
   *  re-pricing it would write a row `snapshotFromSeed` deliberately ignores
   *  (`seedBuilder.ts:242-250`). The price would not move and nothing would
   *  raise an error.
   *
   *  Threads `asOf`, like the value lookup below and every other `resolveAt`
   *  call in this function. It was the one that did not, which was inert only
   *  because no seed has ever carried a KIND change recorded after a basis
   *  instant — and `toSeedRow`'s own doc comment is twenty lines on how an
   *  unthreaded `asOf` silently erases a change. An out-of-band basis here
   *  would resolve the kind against a catalog the value was not read from. */
  const kindAt = (
    rows_: AuthoredRow[], sku: string, quarter: string,
  ): CatalogSeedRow['kind'] => {
    const resolved = resolveAt(rows_, sku, quarter, asOf)?.row.kind;
    if (resolved && resolved !== 'unpriced') return resolved;
    // Terminated or never authored: fall back to the most recent kind that
    // actually carried a cost, else infer from the SKU's role.
    const priced = rows_.filter((r) => r.componentSku === sku && r.kind !== 'unpriced')
                        .sort((a, b) => compareQuarters(a.effectiveFrom, b.effectiveFrom));
    return priced[priced.length - 1]?.kind
        ?? (sku.startsWith('CMP-BASE-') ? 'baseRemainder' : 'component');
  };

  const lastAuthored = new Map<string, string>();
  for (const r of seed.rows) {
    const seen = lastAuthored.get(r.componentSku);
    if (!seen || compareQuarters(r.effectiveFrom, seen) > 0) {
      lastAuthored.set(r.componentSku, r.effectiveFrom);
    }
  }

  const components = matched.matched.filter((c) => c.target?.type === 'component');
  const products = matched.matched.filter((c) => c.target?.type === 'product');

  // ---- Pass 1: component costs -------------------------------------------
  for (const cell of components) {
    const sku = (cell.target as { componentSku: string }).componentSku;

    // KIND GUARD, before anything is scaled (whole-branch review finding C1).
    // `scaleDecimal(_, 6)` below is a µ$ scaling and it used to run for EVERY
    // matched component cell — but `integrationPct`/`discountPct` rows hold
    // BASIS POINTS, so a dollar amount written to one is wrong by 10⁴ and
    // wrong in a direction nothing downstream can detect: `previewImpact`
    // diffed only net prices, which do not depend on `discountBp` at all.
    // Zeroing `CMP-DISC-HW` therefore rendered as `$0.00 → $0.00` over an
    // empty impact table, and published as a 0% hardware discount.
    //
    // Blocked, not silently skipped: the PM typed something into a cell (or a
    // sheet named that ref) and is owed a reason. It is also the single choke
    // point both entry points share, which is why it is not a page-level fix.
    const kind = kindAt(seed.rows, sku, cell.quarter);
    if (!isCostKind(kind)) {
      blocked.push({
        cell,
        reason: 'not-a-cost-kind',
        detail: `'${cell.ref}' resolves to ${sku}, a ${kind} row whose value is in ` +
                `BASIS POINTS, not micro-dollars. Percentages are not authored from ` +
                `this screen — writing a dollar amount here would silently replace ` +
                `the percentage. Nothing was planned for this cell.`,
      });
      continue;
    }

    let value: string;
    try {
      value = scaleDecimal(cell.amount, 6);
    } catch (e) {
      // One bad cell is a review finding, not a reason to lose every other
      // cell's work (review finding #3) — the module's own thesis, applied
      // to the scaling boundary too.
      blocked.push({ cell, reason: 'malformed-amount',
                     detail: e instanceof Error ? e.message : String(e) });
      continue;
    }
    const current = resolveAt(seed.rows, sku, cell.quarter, asOf)?.row.value ?? null;
    const atQuarter = latestAt(seed.rows, sku, cell.quarter);

    if (current !== null && current === value) {
      const last = lastAuthored.get(sku);
      if (!(last !== undefined && compareQuarters(cell.quarter, last) > 0)) {
        noOps.push({ ref: cell.ref, quarter: cell.quarter, amount: cell.amount });
        continue;
      }
      const planned = row(sku, cell.quarter, kind, value, current, atQuarter, true);
      cellOf.set(planned, cell);
      rows.push(planned);
      continue;
    }
    const planned = row(sku, cell.quarter, kind, value, current, atQuarter, false);
    cellOf.set(planned, cell);
    rows.push(planned);
  }

  // Pass 2 prices against the catalog WITH pass 1 applied — see §5.4.
  // `(p) => toSeedRow(p, vendor, asOf)`, NOT a bare `rows.map(toSeedRow)`:
  // Array.prototype.map calls (value, index, array), so the bare form passes the
  // array INDEX as `vendor` and never threads `asOf` at all — and an unthreaded
  // asOf silently erases the whole staged change (see toSeedRow).
  const vendor = seed.rows[0]?.vendor ?? matched.vendor;
  const staged: AuthoredSeed = {
    ...seed, rows: [...seed.rows, ...rows.map((p) => toSeedRow(p, vendor, asOf))],
  };

  // ---- Pass 2: product prices --------------------------------------------
  for (const cell of products) {
    const productSku = (cell.target as { productSku: string }).productSku;
    let targetCents: number;
    try {
      targetCents = Number(scaleDecimal(cell.amount, 2));
    } catch (e) {
      blocked.push({ cell, reason: 'malformed-amount',
                     detail: e instanceof Error ? e.message : String(e) });
      continue;
    }
    const snap = snapshotFromSeed(staged, cell.quarter, asOf);
    const priced = deriveHwPricing(snap)[productSku];

    // Same three-way split as pass 1: unchanged inside the authored range is a
    // no-op; unchanged BEYOND the last authored quarter is the "I checked, still
    // flat" re-assertion and must be RECORDED — and must carry reassertion:true,
    // or ReviewPanel (Task 10) and CatalogPage (Task 12) render it as an ordinary
    // edit. Pass 1 and pass 2 must agree on this; an asymmetry here is invisible
    // until a PM re-states a product price and the review calls it a change.
    let reassertion = false;
    if (priced && priced.discountCents === targetCents) {
      const baseSku = `CMP-BASE-${productSku}`;
      const last = lastAuthored.get(baseSku);
      if (!(last !== undefined && compareQuarters(cell.quarter, last) > 0)) {
        noOps.push({ ref: cell.ref, quarter: cell.quarter, amount: cell.amount });
        continue;
      }
      reassertion = true;
    }

    const baseSku = `CMP-BASE-${productSku}`;
    const composition = snap.compositions[productSku];
    if (!composition) {
      blocked.push({ cell, reason: 'infeasible',
        detail: `${productSku} has no fully-costed composition at ${cell.quarter}` });
      continue;
    }
    const baseEntry = composition.find((c) => c.componentSku === baseSku);
    if (!baseEntry || baseEntry.qty !== 1) {
      blocked.push({ cell, reason: 'infeasible',
        detail: `${productSku}'s composition carries no single ${baseSku} to solve into` });
      continue;
    }

    // `solveBaseMicros` returns the TOTAL uplifted µ$, not the base's share
    // (backDerive.ts:36-45 — its `forward` is b*(10000+bp) + exempt*10000). The
    // other uplifted components must therefore come back out. This is exactly
    // what the catalog's own solver already does: remainderRows.ts:138 is
    // `solveBaseMicros(...) - upliftedKnownMicros`, with CMP-BASE-<sku> skipped
    // from the rollup at :114. Returning the raw solve would make every product
    // price the PM types come out roughly DOUBLE.
    // The base is kind 'baseRemainder', so it is uplifted, so it sits inside
    // upliftedMicros and has to be subtracted back off.
    const { upliftedMicros, exemptMicros } = rollupSplit(productSku, snap);
    const upliftedKnownMicros = upliftedMicros - (snap.componentCostMicros[baseSku] ?? 0);

    // Prefer the canonical zero remainder when the known components ALONE
    // already reproduce the target price (review finding #2) — the same
    // guard remainderRows.ts:131-138 already applies, reused rather than
    // reinvented. solveBaseMicros may return any point within the ~1¢
    // rounding window around the true zero, often a few µ$ NEGATIVE even
    // though the price is exactly reachable at cost; without this check that
    // false negative trips the `solved < 0` guard below and blocks a price a
    // PM is allowed to set.
    const zeroRemainderCents = mulDivHalfEven(
      upliftedKnownMicros * (10000 + snap.integrationBp) + exemptMicros * 10000, 1, 100000000);

    let solved: number;
    if (zeroRemainderCents === targetCents) {
      solved = 0;
    } else {
      try {
        solved = solveBaseMicros(targetCents, snap.integrationBp, exemptMicros)
                 - upliftedKnownMicros;
      } catch (e) {
        // Catch ONLY the typed InfeasibleBaseError (review finding #1):
        // that is the sole `solveBaseMicros` throw site that is a genuine
        // business infeasibility (exempt components alone exceed the
        // target). Every other throw it can produce — an unsafe-integer
        // guard, a non-positive denominator, a convergence failure — is an
        // internal precondition violation, not "the components cannot fit
        // under this price," and mislabeling it as infeasible would hide a
        // real bug behind a plausible-looking review finding. Let it
        // propagate instead.
        if (!(e instanceof InfeasibleBaseError)) throw e;
        blocked.push({ cell, reason: 'infeasible', detail: e.message });
        continue;
      }
      if (solved < 0) {
        blocked.push({ cell, reason: 'negative-remainder',
          detail: `solved base remainder ${solved} µ$ — the modelled components already ` +
                  `cost more than ${productSku} sells for at ${cell.quarter}` });
        continue;
      }
    }

    const current = resolveAt(staged.rows, baseSku, cell.quarter, asOf)?.row.value ?? null;
    const atQuarter = latestAt(staged.rows, baseSku, cell.quarter);
    // The same kind guard pass 1 applies. Unreachable in practice — a product
    // always solves into `CMP-BASE-<sku>`, and `kindAt` infers 'baseRemainder'
    // from that prefix when nothing priced resolves — but the two passes must
    // agree on what may be written, or the invariant is only half true and the
    // half that holds is the one nobody would have broken.
    const baseKind = kindAt(staged.rows, baseSku, cell.quarter);
    if (!isCostKind(baseKind)) {
      blocked.push({
        cell,
        reason: 'not-a-cost-kind',
        detail: `'${cell.ref}' prices ${productSku}, whose base ${baseSku} is a ` +
                `${baseKind} row — a percentage, not a micro-dollar cost. Nothing ` +
                `was planned for this cell.`,
      });
      continue;
    }
    const planned = row(baseSku, cell.quarter, baseKind,
                        String(solved), current, atQuarter, reassertion);
    planned.solvedFor = { productSku, targetCents };
    cellOf.set(planned, cell);
    rows.push(planned);
  }

  // ---- Duplicate-tuple guard ----------------------------------------------
  // A component cell can author CMP-BASE-X directly (unusual, but not
  // prevented) while a product cell in the SAME draft prices X — whose
  // solved remainder ALSO targets CMP-BASE-X. matchDraft's Decision 3 cannot
  // catch this: the two cells have different match targets (a component ref
  // and a product ref), so it never sees a raw duplicate. The two rows only
  // converge on the same (componentSku, effectiveFrom) tuple here, after pass
  // 2 has mapped the product to its base SKU — which is why this check has to
  // live here and run last, over BOTH passes' output together.
  //
  // Left uncaught, this is silent, permanent corruption: `latestAt` picks
  // whichever of the two staged rows happens to look newest, the OTHER one's
  // `previousId`/`previousVersion` come from an in-memory row `toSeedRow`
  // never gave an `id`, and `buildCreateInputs` (Task 7) emits TWO creates
  // for one tuple with deterministic ids that don't collide — both get
  // written. `supersedes` is immutable once written, so there is no later
  // point at which this self-heals; a subsequent commit builds its chain off
  // whichever row it resolves to and never learns the other exists.
  //
  // Neither row is safe to keep and neither is obviously "right" — one PM
  // typed a component cost, a different (or the same) PM typed a product
  // price, and only a human can say which one governs. So both are pulled
  // back out of `rows` and replaced with ONE `Blocked` finding naming both
  // refs and the quarter, exactly the same "review finding, never a row"
  // shape this module already uses for an infeasible or negative remainder.
  const byTuple = new Map<string, PlannedRow[]>();
  for (const r of rows) {
    const key = `${r.componentSku}@${r.effectiveFrom}`;
    const group = byTuple.get(key);
    if (group) group.push(r); else byTuple.set(key, [r]);
  }
  const collided = new Set<PlannedRow>();
  for (const group of byTuple.values()) {
    if (group.length < 2) continue;
    for (const r of group) collided.add(r);
    // Every row this function pushes is registered in cellOf right before
    // the push (three sites, both passes), so this lookup cannot miss.
    const cells = group.map((r) => cellOf.get(r)!);
    // The product cell reads as the more useful anchor for a reviewer — it's
    // the customer price they were trying to set — but `detail` names both
    // refs regardless of which `cell` the entry is filed under.
    const anchor = cells.find((c) => c.target?.type === 'product') ?? cells[0]!;
    const first = group[0]!;
    blocked.push({
      cell: anchor,
      reason: 'duplicate-tuple',
      detail: `${cells.map((c) => `'${c.ref}'`).join(' and ')} both plan a write to ` +
        `(${first.componentSku}, ${first.effectiveFrom}) in this draft — matchDraft cannot ` +
        `catch this because the two cells resolve to different targets. Keep only one of ` +
        `these rows and re-submit; writing both would corrupt the supersession chain.`,
    });
  }
  const deduped = collided.size > 0 ? rows.filter((r) => !collided.has(r)) : rows;

  return { rows: deduped, noOps, blocked };
}

/**
 * The highest-versioned row at this EXACT (componentSku, effectiveFrom) tuple,
 * or undefined when the tuple has never been authored.
 *
 * RETURNS THE ROW, not a scalar — `row(...)` needs both `id` (for `supersedes`)
 * and `version` (for `previousVersion`) off the same record, and splitting them
 * into two lookups is how they drift apart.
 *
 * EXACT effectiveFrom, not `<=`: this answers "am I about to create a second
 * version of an existing tuple?", which is the same question
 * `detectStaleness` asks (Task 7 filters on componentSku && effectiveFrom and
 * reduces to max version, defaulting to 0). If this used resolveAt's
 * carry-forward semantics instead, a first-ever write at a new quarter would
 * claim previousVersion from a CARRIED row, emit version 2, and detectStaleness
 * would then read the real 0 and abort every commit as stale.
 */
export function latestAt(
  rows: AuthoredRow[], componentSku: string, quarter: string,
): AuthoredRow | undefined {
  let best: AuthoredRow | undefined;
  for (const r of rows) {
    if (r.componentSku !== componentSku) continue;
    if (r.effectiveFrom !== quarter) continue;
    if (!best || r.version > best.version) best = r;
  }
  return best;
}

/** Builds a PlannedRow. Positional signature matched to the three call sites
 *  above. `atQuarter` absent means nothing was ever authored at this tuple, so
 *  previousId is null and previousVersion is 0 — which Task 7's
 *  buildCreateInputs turns into version 1 with `supersedes: null`, and which
 *  detectStaleness reads back as "still 0, nobody raced me". */
function row(
  componentSku: string,
  effectiveFrom: string,
  kind: CatalogSeedRow['kind'],
  value: string,
  previousValue: string | null,
  atQuarter: AuthoredRow | undefined,
  reassertion: boolean,
): PlannedRow {
  return {
    componentSku, effectiveFrom, kind, value, previousValue,
    previousId: atQuarter?.id ?? null,
    previousVersion: atQuarter?.version ?? 0,
    reassertion,
  };
}

/**
 * A planned row as an in-memory seed row, so pass 2 (and previewImpact) can
 * price against it before anything is written.
 *
 * `version: previousVersion + 1` is what makes resolveAt's max(version)
 * tie-break pick it over the row it supersedes at the same effectiveFrom — and
 * it is the SAME expression buildCreateInputs uses, which is the only reason
 * preview can be trusted to predict commit.
 *
 * `recordedAt: asOf ?? sentinel` is load-bearing. resolveAt drops any row with
 * `recordedAt > asOf` (resolveAt.ts:53), and the sentinel is greater than every
 * real asOf — so a hardcoded sentinel under an asOf would filter the staged row
 * straight back out. planWrites would then price pass 2 against the UNCHANGED
 * catalog and previewImpact would return [] ("no prices move") for any change
 * whatsoever. The reviewer sees an empty impact table and approves it: exactly
 * the failure the anti-divergence gate exists to prevent, and that gate would
 * NOT catch it, because it passes no asOf.
 */
export function toSeedRow(
  planned: PlannedRow, vendor: string, asOf?: string,
): CatalogSeedRow {
  return {
    vendor,
    effectiveFrom: planned.effectiveFrom,
    componentSku: planned.componentSku,
    kind: planned.kind,
    value: planned.value,
    recordedAt: asOf ?? STAGED_RECORDED_AT,
    version: planned.previousVersion + 1,
  };
}

/** The exact inverse of scaleDecimal — ('6026360000', 6) -> '6026.360000'.
 *  Task 14's inertness gate needs it: a component's authored value is µ$, and a
 *  sheet restating it must carry SIX decimal places or the restatement differs
 *  from the catalog and the "plans nothing" gate fails for a rounding reason.
 *  Never use toFixed here — 8177638888 µ$ is $8177.638888, and toFixed(2)
 *  destroys four digits of a real seed value. */
export function unscaleDecimal(integer: string, scale: number): string {
  const negative = integer.startsWith('-');
  const digits = integer.replace('-', '').padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const frac = digits.slice(digits.length - scale);
  return `${negative ? '-' : ''}${whole}${scale > 0 ? `.${frac}` : ''}`;
}
